"use client";

import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { PaymentProcessor } from "@/types/auth";
import { DebitOrCredit } from "@/types/enums/payment";
import { PaymentPatient } from "@/types/payment";
import { BookingFeePayinResponse } from "@/types/rainforest";
import { tenantQueryOptions } from "@/query/tenant";

/**
 * The bits a card form needs to mount. Both the booking-fee and
 * online-purchase flows funnel into this shape before passing it to
 * `<PayinSessionSurface>`.
 *
 * Carries both processors' halves, like the responses it is built from:
 * Rainforest authenticates the browser with a session key and names the
 * payment by its config id; Stripe authenticates with a publishable key scoped
 * to the connected account and names the payment by a client secret. Dropping
 * the Stripe half here is what made a booking fee unpayable on a Stripe
 * merchant - the mapper kept three Rainforest fields and the surface had
 * nothing to tell it otherwise.
 */
export interface PayinSessionData {
  processor?: PaymentProcessor;
  sessionKey: string;
  payinConfigId: string;
  sandbox: boolean;
  publishableKey?: string | null;
  stripeAccount?: string | null;
  clientSecret?: string | null;
}

/** Retained name, for the call sites that import it. */
export type RainforestPayinSessionData = PayinSessionData;

/** Maps any payin-config mutation result to card-form session data (or null). */
export function payinResponseToSession(response: unknown): PayinSessionData | null {
  if (!response || typeof response !== "object") return null;
  if ("state" in response) {
    const bookingFee = response as BookingFeePayinResponse;
    if (bookingFee.state !== "READY") return null;
    return {
      processor: bookingFee.processor,
      sessionKey: bookingFee.sessionKey,
      payinConfigId: bookingFee.payinConfigId,
      sandbox: bookingFee.sandbox,
      publishableKey: bookingFee.publishableKey,
      stripeAccount: bookingFee.stripeAccount,
      clientSecret: bookingFee.clientSecret
    };
  }
  if ("sessionKey" in response && "payinConfigId" in response) {
    const purchase = response as {
      processor?: PaymentProcessor;
      sessionKey: string;
      payinConfigId: string;
      sandbox?: boolean;
      publishableKey?: string | null;
      stripeAccount?: string | null;
      clientSecret?: string | null;
    };
    return {
      processor: purchase.processor,
      sessionKey: purchase.sessionKey,
      payinConfigId: purchase.payinConfigId,
      sandbox: purchase.sandbox ?? false,
      publishableKey: purchase.publishableKey,
      stripeAccount: purchase.stripeAccount,
      clientSecret: purchase.clientSecret
    };
  }
  return null;
}
export interface UseRainforestPayinSessionArgs<TInput, TResult> {
  /**
   * Mutation that creates a payin config server-side and returns session info.
   * `null` is allowed for callers whose generated client may return null on
   * unexpected (non-throwing) failures; the hook treats it as "no session".
   */
  mutationOptions: {
    mutationFn: (input: TInput) => Promise<TResult | null>;
  };
  /**
   * Build the request body. Receives the current `isSurcharged` flag so the body
   * always reflects the latest BIN-lookup-driven surcharge value.
   */
  buildInput: (args: {
    isSurcharged: boolean;
  }) => TInput;
  /**
   * Whether the effect should fire. Use to gate on "cart non-empty",
   * "appointment created", "patient resolved", etc.
   */
  enabled: boolean;
  /**
   * Authed patient (or buyer) for surcharge overrides. Omit when the payer is
   * not resolved yet; `surchargeDisabled` then defaults to false (surcharge
   * may still apply based on tenant settings and card type).
   */
  patient?: PaymentPatient | null;
  /**
   * Optional stable fingerprint of the inputs that should re-fire the payin
   * config. When this changes, the hook discards any in-flight gate and
   * requests a new session. Use this when the request body can change after
   * the first call (e.g. the shop cart). Self-schedule does not pass this —
   * its booking fee is decided before the checkout step and never changes.
   */
  inputKey?: string;
}

/**
 * Owns the gnarly Rainforest payment-session lifecycle that used to live in
 * `app/[tenant]/self-schedule/steps/checkout.tsx`:
 *
 * - tenant `feeHandling === "SURCHARGE"` × patient `surchargeDisabled` ×
 *   debit/credit detection via Rainforest's BIN lookup → derived `isSurcharged`
 * - re-submit the payin-config request when the surcharge value changes
 *   mid-flight (the value computed *during* a request can differ from the
 *   value submitted *with* the request when the buyer swaps cards mid-pay)
 * - returns a stable `payinResponse` for the caller to render
 *
 * Callers wrap payment UI in `<RainforestPayinSessionProvider>` so
 * `PaymentMethodPicker` can read session + BIN-lookup state from context.
 */
export function useRainforestPayinSession<TInput, TResult>({
  mutationOptions,
  buildInput,
  enabled,
  patient,
  inputKey
}: UseRainforestPayinSessionArgs<TInput, TResult>) {
  const surchargeDisabled = patient?.surchargeDisabled ?? false;
  const [debitOrCredit, setDebitOrCredit] = useState<DebitOrCredit>(DebitOrCredit.CREDIT);
  const [payinResponse, setPayinResponse] = useState<TResult | null>(null);

  // The two-ref + counter pattern guards against an isSurcharged change *during*
  // an in-flight request. Without it, the request submitted with stale=false
  // would land while the user has flipped to true (or vice versa) and the
  // response wouldn't match the visible state. The counter forces a re-fire.
  const lastSubmittedSurchargeRef = useRef<boolean | null>(null);
  const submittedSurchargeValueRef = useRef<boolean | null>(null);
  const [resubmitTrigger, setResubmitTrigger] = useState(0);
  const {
    data: tenantSurcharging,
    isLoading: isTenantLoading
  } = useQuery({
    ...tenantQueryOptions.public(),
    select: data => data?.feeHandling === "SURCHARGE"
  });
  const isSurcharged = (tenantSurcharging ?? false) && !surchargeDisabled && debitOrCredit === DebitOrCredit.CREDIT;

  // Mirror into a ref so the mutation callback can compare against the
  // value that was visible *when the response landed*.
  const isSurchargedRef = useRef<boolean>(isSurcharged);
  isSurchargedRef.current = isSurcharged;
  const currentInputKeyRef = useRef(inputKey);
  currentInputKeyRef.current = inputKey;
  const submittedInputKeyRef = useRef<string | undefined>(undefined);
  const {
    mutate,
    isPending: isCreatingPayinConfig,
    isError
  } = useMutation({
    mutationFn: mutationOptions.mutationFn,
    onSuccess(data) {
      if (submittedInputKeyRef.current !== currentInputKeyRef.current) {
        lastSubmittedSurchargeRef.current = null;
        submittedSurchargeValueRef.current = null;
        setResubmitTrigger(prev => prev + 1);
        return;
      }
      if (data) {
        setPayinResponse(data);
      }
      const currentSurcharged = isSurchargedRef.current;
      if (submittedSurchargeValueRef.current !== currentSurcharged) {
        // The surcharge flipped while we were waiting; force a refire.
        lastSubmittedSurchargeRef.current = null;
        setResubmitTrigger(prev => prev + 1);
      }
    },
    onError() {
      // Clear the gate so the effect can retry on the next legitimate trigger
      // (enabled flip, surcharge change, cart change) rather
      // than wedging forever. Callers that don't read `isError` keep their
      // current visible behavior — there's no automatic refire here.
      lastSubmittedSurchargeRef.current = null;
      submittedSurchargeValueRef.current = null;
    }
  });

  // When the caller-supplied inputKey changes (e.g. cart contents on the shop
  // route), discard the in-flight gate AND the stale session so the effect
  // below kicks off a fresh request and the caller doesn't keep rendering
  // an iframe that prices the previous cart. Self-schedule never passes
  // inputKey, so this is a no-op for that flow.
  const lastInputKeyRef = useRef<string | undefined>(inputKey);
  if (lastInputKeyRef.current !== inputKey) {
    lastInputKeyRef.current = inputKey;
    lastSubmittedSurchargeRef.current = null;
    submittedSurchargeValueRef.current = null;
    if (payinResponse !== null) {
      setPayinResponse(null);
    }
  }
  useEffect(() => {
    if (!enabled || isTenantLoading || isCreatingPayinConfig || lastSubmittedSurchargeRef.current === isSurcharged) {
      return;
    }
    lastSubmittedSurchargeRef.current = isSurcharged;
    submittedSurchargeValueRef.current = isSurcharged;
    submittedInputKeyRef.current = inputKey;
    mutate(buildInput({
      isSurcharged
    }));
    // `isCreatingPayinConfig` is intentionally *not* in deps; the resubmit
    // counter handles the surcharge-changed-mid-flight case explicitly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, isTenantLoading, isSurcharged, mutate, resubmitTrigger, inputKey]);
  return {
    payinResponse,
    isLoadingSession: isCreatingPayinConfig || !payinResponse && enabled && !isError,
    setDebitOrCredit,
    isError
  };
}